import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Button, 
  IconButton, 
  Menu,
  Tooltip,
  CircularProgress
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import TableChartIcon from '@mui/icons-material/TableChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import { themeColors } from '../common/constants';
import { exportSurveyData, exportReportData } from '../../utils/exportUtils';

const Header = ({ 
  tabValue, 
  dateRange, 
  setDateRange, 
  handleOpenFilterDialog, 
  refreshData, 
  sqdScores = {},
  reportData = {},
  isFiltered = false,
  onClearFilters = () => {}
}) => {
  // State for export menu
  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const [exporting, setExporting] = useState(false);
  
  // Handlers for export menu
  const handleExportClick = (event) => {
    setExportAnchorEl(event.currentTarget);
  };
  
  const handleExportClose = () => {
    setExportAnchorEl(null);
  };
  
  // Handle data export
  const handleExportData = async () => {
    setExporting(true);
    try {
      const result = await exportSurveyData(dateRange, null, showExportError);
      if (result.success) {
        showExportSuccess();
      }
    } finally {
      setExporting(false);
      handleExportClose();
    }
  };
  
  // Handle report export
  const handleExportReport = async () => {
    setExporting(true);
    try {
      const result = exportReportData(reportData, sqdScores, dateRange, showExportError);
      if (result.success) {
        showExportSuccess();
      }
    } finally {
      setExporting(false);
      handleExportClose();
    }
  };
  
  // Success and error handling (would be implemented with snackbar in the parent component)
  const showExportSuccess = () => {
    // This would be implemented in the parent component
    console.log('Export successful');
    // You can use the existing snackbar state in AdminDashboard
  };
  
  const showExportError = (message) => {
    // This would be implemented in the parent component
    console.error('Export error:', message);
    // You can use the existing snackbar state in AdminDashboard
  };
  
  // Determine if export button should be visible
  const showExportButton = tabValue === 1 || tabValue === 2; // Only for Feedback and Reports tabs
  
  // Format date for display
  const getDateRangeDisplay = () => {
    switch (dateRange) {
      case 'last7': return 'Last 7 Days';
      case 'last30': return 'Last 30 Days';
      case 'last90': return 'Last 90 Days';
      case 'thisYear': return 'This Year';
      case 'allTime': return 'All Time';
      default: return 'Last 30 Days';
    }
  };

  return (
    <Box sx={{ 
      p: 2, 
      bgcolor: '#FFFFFF', 
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      mb: 3
    }}>
      <Typography variant="h5" sx={{ fontWeight: 'bold', color: themeColors.primary }}>
        {tabValue === 0 && 'Dashboard Overview'}
        {tabValue === 1 && 'Feedback Responses'}
        {tabValue === 2 && 'Reports & Analytics'}
        {tabValue === 3 && 'User Management'}
        {tabValue === 4 && 'Settings'}
      </Typography>
      
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControl sx={{ minWidth: 180, mr: 2 }} disabled={isFiltered}>
          <InputLabel>Date Range</InputLabel>
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            label="Date Range"
            size="small"
          >
            <MenuItem value="last7">Last 7 Days</MenuItem>
            <MenuItem value="last30">Last 30 Days</MenuItem>
            <MenuItem value="last90">Last 90 Days</MenuItem>
            <MenuItem value="thisYear">This Year</MenuItem>
            <MenuItem value="allTime">All Time</MenuItem>
          </Select>
        </FormControl>
        
        {isFiltered ? (
          <Button 
            variant="outlined" 
            onClick={onClearFilters}
            sx={{ 
              borderColor: themeColors.secondary, 
              color: themeColors.secondary,
              mr: 1,
              '&:hover': {
                borderColor: themeColors.secondaryDark,
                backgroundColor: 'rgba(0, 128, 128, 0.04)'
              }
            }}
          >
            Clear Filters
          </Button>
        ) : null}
        
        <Button 
          variant="outlined" 
          startIcon={<FilterListIcon />}
          onClick={handleOpenFilterDialog}
          color={isFiltered ? "secondary" : "primary"}
          sx={{ 
            mr: 1,
            '&:hover': {
              backgroundColor: isFiltered ? 'rgba(0, 128, 128, 0.04)' : 'rgba(128, 0, 0, 0.04)'
            }
          }}
        >
          {isFiltered ? "Edit Filters" : "Filter"}
        </Button>
        
        {showExportButton && (
          <>
            <Button 
              variant="outlined" 
              startIcon={exporting ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleExportClick}
              disabled={exporting}
              sx={{ 
                borderColor: themeColors.primary, 
                color: themeColors.primary,
                mr: 1,
                '&:hover': {
                  borderColor: themeColors.primaryDark,
                  backgroundColor: 'rgba(128, 0, 0, 0.04)'
                }
              }}
            >
              Export
            </Button>
            
            <Menu
              anchorEl={exportAnchorEl}
              open={Boolean(exportAnchorEl)}
              onClose={handleExportClose}
            >
              <MenuItem onClick={handleExportData}>
                <TableChartIcon fontSize="small" sx={{ mr: 1 }} />
                Export Raw Data ({getDateRangeDisplay()})
              </MenuItem>
              
              {tabValue === 2 && (
                <MenuItem onClick={handleExportReport}>
                  <BarChartIcon fontSize="small" sx={{ mr: 1 }} />
                  Export Report Summary
                </MenuItem>
              )}
            </Menu>
          </>
        )}
        
        <Tooltip title="Refresh Data">
          <IconButton 
            onClick={refreshData}
            sx={{ 
              color: themeColors.primary,
              '&:hover': {
                backgroundColor: 'rgba(128, 0, 0, 0.04)'
              }
            }}
          >
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

export default Header;